// src/pages/services/SoftwareDevelopment.jsx - Enterprise Software Solutions
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, Code, Smartphone, Monitor, Globe, Database, Zap, ArrowRight, ArrowLeft, Rocket, Users, Shield, ExternalLink } from 'lucide-react';
import RelatedServices from '../../components/RelatedServices';

export default function SoftwareDevelopment() {
  const [openFAQ, setOpenFAQ] = useState(null);

  const offerings = [
    {
      icon: <Globe className="w-6 h-6 sm:w-8 sm:h-8" />,
      title: "Web Application Development",
      description: "Scalable, responsive web applications",
      features: ['React/Vue/Angular', 'RESTful APIs', 'Real-time Updates', 'Progressive Web Apps']
    },
    {
      icon: <Smartphone className="w-6 h-6 sm:w-8 sm:h-8" />,
      title: "Mobile App Development",
      description: "Native and cross-platform applications",
      features: ['iOS/Android', 'React Native', 'Flutter', 'App Store Optimization']
    },
    {
      icon: <Monitor className="w-6 h-6 sm:w-8 sm:h-8" />,
      title: "Desktop Applications",
      description: "Cross-platform desktop solutions",
      features: ['Electron', 'Windows/macOS/Linux', 'Native Performance', 'Auto Updates']
    },
    {
      icon: <Database className="w-6 h-6 sm:w-8 sm:h-8" />,
      title: "API Development",
      description: "RESTful and GraphQL APIs",
      features: ['Microservices', 'Authentication', 'Rate Limiting', 'Documentation']
    },
    {
      icon: <Code className="w-6 h-6 sm:w-8 sm:h-8" />,
      title: "Custom Software Solutions",
      description: "Tailored solutions for your business",
      features: ['Business Logic', 'Integration', 'Automation', 'Scalability']
    },
    {
      icon: <Zap className="w-6 h-6 sm:w-8 sm:h-8" />,
      title: "Legacy System Modernization",
      description: "Upgrade and transform existing systems",
      features: ['Code Refactoring', 'Migration', 'Performance', 'Security']
    }
  ];

  const technologies = [
    { category: "Frontend", items: ["React.js", "Angular", "Vue.js", "Next.js", "TypeScript"] },
    { category: "Backend", items: ["Node.js", "Python", "Java", "Go", ".NET Core"] },
    { category: "Mobile", items: ["React Native", "Flutter", "iOS", "Android"] },
    { category: "Database", items: ["PostgreSQL", "MongoDB", "MySQL", "Redis"] },
    { category: "Cloud", items: ["AWS", "Azure", "Google Cloud", "Docker"] },
    { category: "Tools", items: ["Git", "Jenkins", "Jira", "VS Code"] }
  ];

  const stats = [
    { value: "50+", label: "Projects Delivered" },
    { value: "30+", label: "Enterprise Clients" },
    { value: "99%", label: "Uptime Average" },
    { value: "24/7", label: "Support Available" }
  ];

  const benefits = [
    { title: 'Faster Time-to-Market', value: '60% Faster', description: 'Agile development methodology' },
    { title: 'Cost Efficiency', value: '40% Savings', description: 'Optimized development process' },
    { title: 'Code Quality', value: '99%+ Quality', description: 'Rigorous testing & reviews' },
    { title: 'Scalability', value: '∞ Growth', description: 'Built to scale with you' }
  ];

  const pricingPlans = [
    {
      name: 'Startup Package',
      price: 49999,
      monthlySupport: 799,
      color: 'blue',
      badge: null,
      features: [
        'Web or Mobile MVP',
        'Core Features Only',
        'Single Platform',
        '3 Months Support Included',
        'Basic Deployment',
        'Documentation'
      ]
    },
    {
      name: 'Business Solution',
      price: 149999,
      monthlySupport: 3999,
      color: 'indigo',
      badge: 'MOST POPULAR',
      featured: true,
      features: [
        'Full-Stack Application',
        'Web + Mobile (iOS/Android)',
        'Advanced Features',
        '6 Months Support Included',
        'API Development',
        'Analytics Integration',
        'Security & Compliance'
      ]
    },
    {
      name: 'Enterprise Platform',
      price: null,
      monthlySupport: 9999,
      color: 'purple',
      badge: null,
      features: [
        'Complex Enterprise Systems',
        'Dedicated Development Team',
        'Multi-platform Support',
        '24/7 Priority Support',
        'Custom Integrations',
        'Performance Optimization',
        'Continuous Deployment'
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      {/* Hero Section */}
      <section className="relative py-12 sm:py-20 bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-600 text-white px-2 sm:px-4">
        <div className="max-w-7xl mx-auto">
          <Link to="/services" className="inline-flex items-center gap-2 text-white hover:underline mb-4 sm:mb-6 text-xs sm:text-base font-medium">
            <ArrowLeft className="w-3 h-3 sm:w-4 sm:h-4" /> Back to Services
          </Link>
          <div className="grid lg:grid-cols-2 gap-6 sm:gap-12 items-center">
            <div>
              <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-black mb-4 sm:mb-6 leading-tight">
                Software Development Services
              </h1>
              <p className="text-base sm:text-lg lg:text-xl text-blue-100 mb-6 sm:mb-8 leading-relaxed">
                Transform your ideas into powerful, scalable software solutions. From MVPs to enterprise platforms, we deliver excellence.
              </p>
              <div className="flex flex-col gap-3 sm:gap-4">
                <Link to="/contact" className="px-4 sm:px-8 py-2.5 sm:py-4 bg-white text-blue-600 rounded-lg font-semibold hover:bg-gray-100 transition-colors text-center text-sm sm:text-base shadow-lg">
                  Start Your Project
                </Link>
                <Link to="/portfolio" className="px-4 sm:px-8 py-2.5 sm:py-4 bg-transparent border-2 border-white rounded-lg font-semibold hover:bg-white/10 transition-colors text-center text-sm sm:text-base">
                  View Portfolio
                </Link>
              </div>
            </div>
            <div className="hidden lg:block">
              <img src="https://images.unsplash.com/photo-1461749280684-dccba630e2f6?w=600&h=400&fit=crop" alt="Software Development" className="rounded-2xl shadow-2xl w-full" />
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-8 sm:py-16 bg-gray-50 dark:bg-gray-800 px-2 sm:px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-8">
            {benefits.map((benefit, index) => (
              <div key={index} className="text-center bg-white dark:bg-gray-900 p-3 sm:p-6 rounded-xl shadow-lg hover:shadow-xl transition border border-gray-200/50 dark:border-gray-700/50 group">
                <div className="text-2xl sm:text-4xl font-black text-blue-600 mb-1 sm:mb-2 group-hover:scale-110 transition-transform">{benefit.value}</div>
                <div className="text-xs sm:text-lg font-bold text-gray-900 dark:text-white mb-1">{benefit.title}</div>
                <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">{benefit.description}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-12 sm:py-20 px-2 sm:px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-2 sm:mb-4">
              Our Development Services
            </h2>
            <p className="text-sm sm:text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto px-2">
              Comprehensive software development from concept to deployment and beyond
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-8">
            {offerings.map((offering, index) => (
              <div
                key={index}
                className="bg-white dark:bg-gray-800 rounded-xl p-4 sm:p-6 shadow-md border border-gray-200 dark:border-gray-700 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
              >
                <div className="w-12 h-12 sm:w-16 sm:h-16 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center text-blue-600 mb-3 sm:mb-4">
                  {offering.icon}
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white mb-2 sm:mb-3">{offering.title}</h3>
                <p className="text-xs sm:text-base text-gray-600 dark:text-gray-400 mb-3 sm:mb-4">{offering.description}</p>
                <ul className="space-y-1.5 sm:space-y-2">
                  {offering.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-xs sm:text-sm text-gray-700 dark:text-gray-400">
                      <Check className="w-3 h-3 sm:w-4 sm:h-4 text-green-500 flex-shrink-0" /> {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Development Excellence Section - Focused on Capabilities */}
      <section className="py-12 sm:py-20 lg:py-24 bg-gradient-to-br from-blue-600/10 via-indigo-600/10 to-purple-600/10 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl lg:max-w-7xl mx-auto">
          <div className="bg-white/90 dark:bg-gray-900/90 backdrop-blur-xl rounded-3xl p-6 sm:p-8 lg:p-12 shadow-2xl border border-blue-200/50 dark:border-blue-800/50">
            <div className="flex flex-col lg:flex-row items-center lg:items-start gap-8 lg:gap-12">
              {/* Left Content - Vision & Capabilities */}
              <div className="lg:w-1/2">
                <div className="text-center lg:text-left">
                  <Code className="w-16 h-16 sm:w-20 sm:h-20 lg:w-24 lg:h-24 text-blue-600 mx-auto lg:mx-0 mb-4 lg:mb-6 opacity-75 animate-pulse" />
                  <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 bg-clip-text text-transparent mb-4 lg:mb-6">
                    Custom Software Development Solutions
                  </h2>
                  <p className="text-base sm:text-lg lg:text-xl text-gray-700 dark:text-gray-300 mb-6 lg:mb-8 leading-relaxed">
                    We build tailored software solutions that grow with your business. Whether you need a startup MVP or an enterprise system, 
                    <strong className="font-semibold text-blue-600 dark:text-blue-400 block lg:inline"> we deliver quality code and scalable architecture</strong>. 
                    Tell us your requirements, and we'll build exactly what you need.
                  </p>
                </div>

                {/* Development Capabilities - Desktop */}
                <div className="hidden lg:grid grid-cols-2 gap-6 mb-8">
                  <div className="group p-6 bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30 rounded-2xl border border-blue-200/50 hover:shadow-2xl transition-all duration-300 hover:-translate-y-2">
                    <div className="flex items-center gap-4">
                      <Smartphone className="w-12 h-12 text-blue-600 group-hover:scale-110 transition-transform" />
                      <div>
                        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Web & Mobile Apps</h3>
                        <div className="space-y-1">
                          <div className="text-lg font-medium text-gray-600 dark:text-gray-400">Responsive Design</div>
                          <div className="text-lg font-bold text-indigo-600">Cross-platform Ready</div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="group p-6 bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30 rounded-2xl border border-blue-200/50 hover:shadow-2xl transition-all duration-300 hover:-translate-y-2">
                    <div className="flex items-center gap-4">
                      <Database className="w-12 h-12 text-blue-600 group-hover:scale-110 transition-transform" />
                      <div>
                        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Database Systems</h3>
                        <div className="space-y-1">
                          <div className="text-lg font-medium text-gray-600 dark:text-gray-400">Secure & Scalable</div>
                          <div className="text-lg font-bold text-indigo-600">Optimized Performance</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Desktop CTA Buttons */}
                <div className="hidden lg:flex flex-col gap-4">
                  <Link to="/contact" className="px-8 py-4 bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 text-white rounded-2xl font-black text-lg shadow-2xl hover:shadow-3xl transform hover:-translate-y-1 transition-all duration-300 text-center tracking-wide flex items-center justify-center gap-3">
                    💻 Start Your Project
                    <ArrowRight className="w-5 h-5" />
                  </Link>
                  <Link to="/portfolio" className="px-8 py-4 border-2 border-blue-600 text-blue-600 dark:text-blue-400 rounded-2xl font-black text-lg hover:bg-blue-600 hover:text-white transition-all duration-300 text-center tracking-wide flex items-center justify-center gap-3">
                    View Our Work
                    <ExternalLink className="w-5 h-5" />
                  </Link>
                </div>
              </div>

              {/* Right Content - What We Build */}
              <div className="lg:w-1/2">
                <div className="grid grid-cols-2 gap-4 sm:gap-6 lg:gap-8">
                  <div className="group p-4 sm:p-6 lg:p-8 bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30 rounded-2xl border border-blue-200/50 hover:shadow-2xl transition-all duration-300 hover:-translate-y-2">
                    <Shield className="w-10 h-10 sm:w-12 sm:h-12 lg:w-16 lg:h-16 text-blue-600 mx-auto mb-3 sm:mb-4 group-hover:scale-110 transition-transform" />
                    <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 dark:text-white mb-2 lg:mb-3 text-center">Secure Systems</h3>
                    <div className="space-y-1 sm:space-y-2 text-center">
                      <div className="text-lg sm:text-xl lg:text-xl font-bold text-blue-600">Best Practices</div>
                      <div className="text-base sm:text-lg font-medium text-indigo-600">Data Protection</div>
                    </div>
                  </div>
                  
                  <div className="group p-4 sm:p-6 lg:p-8 bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30 rounded-2xl border border-blue-200/50 hover:shadow-2xl transition-all duration-300 hover:-translate-y-2">
                    <Zap className="w-10 h-10 sm:w-12 sm:h-12 lg:w-16 lg:h-16 text-blue-600 mx-auto mb-3 sm:mb-4 group-hover:scale-110 transition-transform" />
                    <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 dark:text-white mb-2 lg:mb-3 text-center">High Performance</h3>
                    <div className="space-y-1 sm:space-y-2 text-center">
                      <div className="text-lg sm:text-xl lg:text-xl font-bold text-blue-600">Optimized Code</div>
                      <div className="text-base sm:text-lg font-medium text-indigo-600">Fast Response</div>
                    </div>
                  </div>
                  
                  <div className="group p-4 sm:p-6 lg:p-8 bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30 rounded-2xl border border-blue-200/50 hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 lg:col-span-2">
                    <div className="flex flex-col sm:flex-row items-center gap-4 lg:gap-6">
                      <div className="text-4xl">🛠️</div>
                      <div className="text-center sm:text-left">
                        <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 dark:text-white mb-2">All Software Types</h3>
                        <div className="text-xl sm:text-2xl lg:text-2xl font-bold text-blue-600">Web, Mobile, Desktop & Cloud</div>
                        <p className="text-sm lg:text-base text-gray-600 dark:text-gray-400">Tell us what you need, we'll build it</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Development Process - Mobile */}
                <div className="mt-6 lg:hidden">
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div className="group p-4 bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30 rounded-xl border border-blue-200/50">
                      <div className="text-center">
                        <div className="text-lg font-bold text-gray-900 dark:text-white">Planning</div>
                        <div className="text-sm text-blue-600">Requirements & Design</div>
                      </div>
                    </div>
                    <div className="group p-4 bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30 rounded-xl border border-blue-200/50">
                      <div className="text-center">
                        <div className="text-lg font-bold text-gray-900 dark:text-white">Development</div>
                        <div className="text-sm text-blue-600">Coding & Testing</div>
                      </div>
                    </div>
                  </div>

                  {/* Mobile/Tablet CTA Buttons */}
                  <div className="flex flex-col gap-3 sm:gap-4">
                    <Link to="/contact" className="px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 text-white rounded-2xl font-black text-sm sm:text-base shadow-2xl hover:shadow-3xl transform hover:-translate-y-1 transition-all duration-300 text-center tracking-wide">
                      💻 Start Your Project
                    </Link>
                    <Link to="/portfolio" className="px-6 sm:px-8 py-3 sm:py-4 border-2 sm:border-4 border-blue-600 text-blue-600 dark:text-blue-400 rounded-2xl font-black text-sm sm:text-base hover:bg-blue-600 hover:text-white transition-all duration-300 text-center tracking-wide">
                      View Our Work
                    </Link>
                  </div>
                </div>
              </div>
            </div>

            {/* Development Capabilities - Desktop Only */}
            <div className="hidden lg:grid grid-cols-4 gap-4 mt-12 pt-8 border-t border-blue-200/50 dark:border-blue-800/50">
              {[
                { label: 'Web Apps', value: 'Responsive', icon: '🌐', description: 'Frontend & Backend' },
                { label: 'Mobile Apps', value: 'iOS & Android', icon: '📱', description: 'Native & Hybrid' },
                { label: 'Desktop Software', value: 'Windows/macOS', icon: '💻', description: 'Cross-platform' },
                { label: 'APIs', value: 'REST & GraphQL', icon: '⚡', description: 'Integration Ready' }
              ].map((capability, index) => (
                <div key={index} className="text-center p-4 bg-gradient-to-br from-blue-50/50 to-indigo-50/50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-xl hover:shadow-lg transition-all duration-300">
                  <div className="text-3xl mb-2">{capability.icon}</div>
                  <div className="text-2xl font-black text-blue-600">{capability.value}</div>
                  <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">{capability.label}</div>
                  <div className="text-xs text-gray-500 dark:text-gray-500 mt-1">{capability.description}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Technologies */}
      <section className="py-12 sm:py-20 bg-gray-50 dark:bg-gray-800 px-2 sm:px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-2 sm:mb-4">
              Technologies & Frameworks
            </h2>
            <p className="text-sm sm:text-lg text-gray-600 dark:text-gray-400 px-2">
              Cutting-edge tools and frameworks for modern software
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-8">
            {technologies.map((tech, index) => (
              <div key={index} className="bg-white dark:bg-gray-900 rounded-xl p-4 sm:p-6 shadow-md border border-gray-200 dark:border-gray-700 hover:shadow-xl transition-all">
                <h3 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white mb-3 sm:mb-4">{tech.category}</h3>
                <div className="flex flex-wrap gap-2">
                  {tech.items.map((item, i) => (
                    <span key={i} className="px-2 sm:px-3 py-1 sm:py-1.5 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 text-xs sm:text-sm rounded-full font-medium border border-blue-200/50 dark:border-blue-700/50">
                      {item}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-12 sm:py-20 px-2 sm:px-4">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-center mb-8 sm:mb-12 text-gray-900 dark:text-white">
            Our Development Process
          </h2>
          <div className="grid md:grid-cols-5 gap-3 sm:gap-6">
            {[
              { step: '1', title: 'Discovery', desc: 'Understand requirements' },
              { step: '2', title: 'Design', desc: 'Architecture & UX' },
              { step: '3', title: 'Development', desc: 'Build with quality' },
              { step: '4', title: 'Testing', desc: 'Quality assurance' },
              { step: '5', title: 'Deployment', desc: 'Launch & monitor' }
            ].map((item, index) => (
              <div key={index} className="text-center group">
                <div className="w-14 h-14 sm:w-20 sm:h-20 bg-gradient-to-br from-blue-600 to-indigo-600 text-white rounded-full flex items-center justify-center text-lg sm:text-2xl font-black mx-auto mb-2 sm:mb-4 shadow-lg group-hover:scale-110 transition-transform">
                  {item.step}
                </div>
                <h3 className="text-sm sm:text-lg font-bold mb-1 sm:mb-2 text-gray-900 dark:text-white">{item.title}</h3>
                <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-300">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-16 sm:py-24 bg-gradient-to-b from-white via-gray-50 to-gray-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-800 px-2 sm:px-4">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12 sm:mb-20">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-black text-gray-900 dark:text-white mb-3 sm:mb-4">
              Software Development Pricing
            </h2>
            <p className="text-base sm:text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto px-2 leading-relaxed">
              Flexible pricing for projects of all sizes. Transparent, no hidden costs.
            </p>
          </div>

          {/* Pricing Cards */}
          <div className="grid md:grid-cols-3 gap-6 sm:gap-8 max-w-6xl mx-auto mb-12 sm:mb-20">
            {pricingPlans.map((plan, idx) => {
              const colorMap = {
                blue: { bg: 'from-blue-600 to-indigo-600', light: 'from-blue-50 to-indigo-50', hover: 'hover:border-blue-200' },
                indigo: { bg: 'from-indigo-600 to-purple-600', light: 'from-indigo-50 to-purple-50', hover: 'hover:border-indigo-200' },
                purple: { bg: 'from-purple-600 to-blue-600', light: 'from-purple-50 to-blue-50', hover: 'hover:border-purple-200' }
              };
              const colors = colorMap[plan.color];

              return (
                <div
                  key={idx}
                  className={`group relative ${plan.featured ? 'md:scale-105' : ''} bg-white dark:bg-gray-900/80 backdrop-blur-sm p-6 sm:p-8 rounded-2xl shadow-lg dark:shadow-2xl border border-gray-200/60 dark:border-gray-700/60 hover:shadow-xl transition-all duration-300 flex flex-col ${colors.hover} dark:hover:border-gray-600/50 ${plan.featured ? 'border-2 border-indigo-500 dark:border-indigo-600' : ''}`}
                >
                  {/* Glow effect for featured */}
                  {plan.featured && (
                    <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/10 to-purple-500/10 dark:from-indigo-600/20 dark:to-purple-600/20 rounded-2xl opacity-100 -z-10 blur-xl"></div>
                  )}

                  {/* Badge */}
                  {plan.badge && (
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                      <div className={`bg-gradient-to-r ${colors.bg} text-white px-4 sm:px-6 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-black shadow-lg`}>
                        {plan.badge} ⭐
                      </div>
                    </div>
                  )}

                  <h3 className={`text-2xl sm:text-3xl font-black text-gray-900 dark:text-white mb-2 ${plan.featured ? 'mt-4 sm:mt-6' : ''}`}>{plan.name}</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-6 font-medium">
                    {plan.name === 'Startup Package' && 'Perfect for initial launch'}
                    {plan.name === 'Business Solution' && 'Best for scalable growth'}
                    {plan.name === 'Enterprise Platform' && 'Complex custom systems'}
                  </p>

                  {/* Price */}
                  <div className="mb-8">
                    {plan.price ? (
                      <>
                        <div className="flex items-baseline gap-1 mb-2">
                          <span className="text-4xl sm:text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r" style={{backgroundImage: `linear-gradient(to right, ${plan.color === 'blue' ? '#2563eb, #4f46e5' : plan.color === 'indigo' ? '#4f46e5, #9333ea' : '#9333ea, #2563eb'})`}}>
                            ${plan.price.toLocaleString()}
                          </span>
                          <span className="text-gray-600 dark:text-gray-400 font-semibold">one-time</span>
                        </div>
                        <div className="flex items-baseline gap-1">
                          <span className="text-2xl sm:text-3xl font-black text-transparent bg-clip-text bg-gradient-to-r" style={{backgroundImage: `linear-gradient(to right, ${plan.color === 'blue' ? '#2563eb, #4f46e5' : plan.color === 'indigo' ? '#4f46e5, #9333ea' : '#9333ea, #2563eb'})`}}>
                            ${plan.monthlySupport.toLocaleString()}
                          </span>
                          <span className="text-gray-600 dark:text-gray-400 font-semibold">/month</span>
                        </div>
                      </>
                    ) : (
                      <div>
                        <div className="text-4xl sm:text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r" style={{backgroundImage: `linear-gradient(to right, ${plan.color === 'blue' ? '#2563eb, #4f46e5' : plan.color === 'indigo' ? '#4f46e5, #9333ea' : '#9333ea, #2563eb'})`}}>
                          ${plan.monthlySupport.toLocaleString()}
                        </div>
                        <span className="text-gray-600 dark:text-gray-400 font-semibold">/month retainer</span>
                      </div>
                    )}
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 font-medium">Custom scoping available</p>
                  </div>

                  {/* Features */}
                  <ul className="space-y-3 sm:space-y-4 mb-8 sm:mb-10 flex-grow">
                    {plan.features.map((feature, featureIdx) => (
                      <li key={featureIdx} className="flex items-start gap-3">
                        <div className={`w-5 h-5 sm:w-6 sm:h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5`} style={{backgroundColor: `${plan.color === 'blue' ? '#dbeafe' : plan.color === 'indigo' ? '#e0e7ff' : '#f3e8ff'}`}}>
                          <Check className="w-3 h-3 sm:w-4 sm:h-4 font-bold" style={{color: `${plan.color === 'blue' ? '#2563eb' : plan.color === 'indigo' ? '#4f46e5' : '#9333ea'}`}} />
                        </div>
                        <span className="text-sm sm:text-base text-gray-700 dark:text-gray-300 font-medium">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  {/* CTA Button */}
                  <Link
                    to="/contact"
                    className={`w-full px-6 py-3 sm:py-4 rounded-xl font-bold shadow-lg hover:shadow-xl transition-all text-sm sm:text-base transform hover:-translate-y-1 text-center ${
                      plan.featured
                        ? `bg-gradient-to-r ${colors.bg} text-white hover:shadow-2xl`
                        : 'bg-gray-900 dark:bg-white text-white dark:text-gray-900 hover:bg-gray-800 dark:hover:bg-gray-100'
                    }`}
                  >
                    {plan.name === 'Enterprise Platform' ? 'Let\'s Talk' : 'Get Started'}
                  </Link>
                </div>
              );
            })}
          </div>

          {/* All Plans Include */}
          <div className="max-w-5xl mx-auto mb-12 sm:mb-16">
            <h3 className="text-center text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-8">All Plans Include</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6">
              {[
                { icon: '🏗️', text: 'Architecture Design' },
                { icon: '🧪', text: 'Automated Testing' },
                { icon: '📊', text: 'Analytics Setup' },
                { icon: '📚', text: 'Full Documentation' }
              ].map((item, idx) => (
                <div key={idx} className="flex items-center gap-3 p-4 bg-white dark:bg-gray-800/50 rounded-lg border border-gray-200/50 dark:border-gray-700/50 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                  <span className="text-2xl">{item.icon}</span>
                  <span className="text-sm sm:text-base text-gray-700 dark:text-gray-300 font-medium">{item.text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* FAQ Section */}
          <div className="max-w-3xl mx-auto bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-2xl border border-blue-200/50 dark:border-blue-800/50 p-6 sm:p-10">
            <h3 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-6 text-center">Software Development FAQ</h3>
            <div className="space-y-4 sm:space-y-5">
              {[
                {
                  q: 'How long does a software project take?',
                  a: 'MVP: 3-4 months. Business app: 4-6 months. Enterprise system: 6-12+ months. We provide detailed timelines after discovery phase and maintain agile sprints for flexibility.'
                },
                {
                  q: 'What is your development methodology?',
                  a: 'We follow Agile/Scrum with 2-week sprints, daily standups, and regular demos. You get continuous visibility, frequent updates, and the ability to adjust requirements based on feedback.'
                },
                {
                  q: 'Do you handle DevOps and deployment?',
                  a: 'Yes! We handle cloud setup (AWS, Azure, GCP), CI/CD pipelines, containerization, monitoring, and continuous deployment. Your app is production-ready on day one.'
                },
                {
                  q: 'What about maintenance and support after launch?',
                  a: 'All plans include post-launch support for bug fixes, security updates, and performance optimization. We also offer extended support packages for ongoing feature development.'
                },
                {
                  q: 'Can you integrate with our existing systems?',
                  a: 'Absolutely! We integrate with legacy systems, third-party APIs, payment gateways, databases, and more. We handle complex data migrations and ensure seamless connectivity.'
                }
              ].map((faq, idx) => (
                <details key={idx} className="group cursor-pointer">
                  <summary className="flex items-center justify-between font-semibold text-gray-900 dark:text-white py-3 px-4 bg-white dark:bg-gray-800/50 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                    <span className="text-sm sm:text-base text-left pr-4">{faq.q}</span>
                    <span className="group-open:rotate-180 transition-transform flex-shrink-0">▼</span>
                  </summary>
                  <p className="text-sm text-gray-600 dark:text-gray-300 p-4 bg-white/50 dark:bg-gray-800/30">{faq.a}</p>
                </details>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 sm:py-20 bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 text-white px-2 sm:px-4">
        <div className="max-w-4xl mx-auto text-center">
          <Rocket className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-4 sm:mb-6 animate-bounce" />
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-black mb-4 sm:mb-6">
            Ready to Build Something Great?
          </h2>
          <p className="text-base sm:text-xl text-blue-100 mb-6 sm:mb-8 px-2">
            Let's transform your vision into powerful software. Start with a free consultation.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 px-6 sm:px-10 py-3 sm:py-5 bg-white text-blue-600 rounded-2xl font-black text-base sm:text-xl shadow-2xl hover:shadow-3xl hover:-translate-y-1 transition-all duration-300"
          >
            <span>💻 Get Free Consultation</span>
            <ArrowRight className="w-4 h-4 sm:w-6 sm:h-6" />
          </Link>
        </div>
      </section>
      <div className="mt-16">
      <RelatedServices currentService="software-development" />
      </div>
    </div>
  );
}